import React, { useMemo } from 'react';
import { Polygon, Tooltip } from 'react-leaflet';
import { normalizePoint } from 'features/Responder/features/Zones';
import { sortZonesForRender } from 'features/Responder/features/Zones';

const toOpacity = (value, fallback = 0.18) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.min(1, num));
};

export default function ZoneLayer({
  zones = [],
  selectedZoneIds = [],
  isolatedLayerDepth = null,
  interactive = true,
  onFocusZone = () => {},
  onContextZone = () => {},
}) {
  const selectedKeys = useMemo(
    () => new Set(selectedZoneIds.map((zoneId) => `${zoneId}`)),
    [selectedZoneIds],
  );

  const polygonPositionsByZone = useMemo(() => {
    const positionsByZone = new Map();
    if (!Array.isArray(zones)) return positionsByZone;

    zones.forEach((zone) => {
      const polygon = (Array.isArray(zone?._points) ? zone._points : [])
        .map((point) => {
          const normalizedPoint = normalizePoint(point);
          return normalizedPoint ? [normalizedPoint.lat, normalizedPoint.lng] : null;
        })
        .filter(Boolean);
      positionsByZone.set(zone, polygon);
    });

    return positionsByZone;
  }, [zones]);

  const renderZones = useMemo(() => {
    if (!Array.isArray(zones)) return [];
    const filtered = zones.filter((zone) => {
      if (!selectedKeys.size) return false;
      if (!selectedKeys.has(`${zone.id}`)) return false;
      if (isolatedLayerDepth == null) return true;
      const depth = Number(zone.layer ?? zone.depth);
      return Number.isFinite(depth) ? depth === isolatedLayerDepth : false;
    });
    return sortZonesForRender(filtered);
  }, [isolatedLayerDepth, selectedKeys, zones]);

  return renderZones.map((zone) => {
    const polygon = polygonPositionsByZone.get(zone) || [];
    if (!polygon.length) return null;
    const fillColor = zone.fillColor || '#F9703E';
    const strokeColor = zone.strokeColor || fillColor;
    const fillOpacity = toOpacity(zone.fillOpacity, 0.15);
    return (
      <Polygon
        key={`zone-${zone.id}`}
        positions={polygon}
        interactive={interactive}
        pathOptions={{
          color: strokeColor,
          fillColor,
          fillOpacity,
          weight: 2,
          className: 'responder-zone-shape',
        }}
        eventHandlers={
          interactive
            ? {
                click: (e) => {
                  const map = e?.target?._map;
                  onFocusZone(zone, map);
                },
                contextmenu: (e) => {
                  if (e?.originalEvent) {
                    e.originalEvent.preventDefault?.();
                    e.originalEvent.stopPropagation?.();
                    e.originalEvent.cancelBubble = true;
                  }
                  const map = e?.target?._map;
                  onContextZone(zone, map, e);
                },
              }
            : undefined
        }
      >
        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
          <div className="fw-semibold">{zone.name || 'Zone'}</div>
          {Number.isFinite(zone?.depth) && (
            <div className="small text-muted">Depth {zone.depth}</div>
          )}
          <div className="small text-muted">
            {zone.includeInParent === false
              ? 'Excluded from parent totals'
              : 'Included in parent totals'}
          </div>
        </Tooltip>
      </Polygon>
    );
  });
}

